import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { seminarService } from '../../api/services';
import {
  Calendar, MapPin, Users, Copy, CheckCircle2,
  ChevronLeft, ChevronRight, Star, Briefcase,
  Play, ExternalLink,
} from 'lucide-react';

// ── Slide types ───────────────────────────────────────────────
const SeminarSlide = ({ item }) => {
  const [copied,    setCopied]    = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const date = item.date
    ? new Date(item.date).toLocaleDateString('en-GH', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  const time = item.date
    ? new Date(item.date).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })
    : '';

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(item.joinCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // If promo video is playing, show it fullscreen in card
  if (showVideo && item.promoVideoUrl) {
    return (
      <div className="relative w-full h-full rounded-2xl overflow-hidden bg-black">
        {item.promoVideoUrl?.includes('youtube.com/embed') ? (
          <iframe
            src={`${item.promoVideoUrl}?autoplay=1&rel=0`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
            allowFullScreen
            className="w-full h-full border-0"
            title="Promo"
          />
        ) : (
          <video src={item.promoVideoUrl} autoPlay controls className="w-full h-full object-cover" />
        )}
        <button
          onClick={() => setShowVideo(false)}
          className="absolute top-3 right-3 w-7 h-7 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/80 transition-colors z-10">
          <span className="text-xs font-bold">✕</span>
        </button>
        {/* Join code overlay */}
        <Link to={`/seminar/${item.joinCode}`}
          className="absolute bottom-3 left-3 right-3 bg-forest-900/90 border border-forest-700 rounded-xl px-3 py-2 flex items-center justify-between">
          <div>
            <p className="text-[9px] text-forest-400 uppercase tracking-widest">Click to join live</p>
            <p className="font-mono font-bold text-white text-sm tracking-widest">{item.joinCode}</p>
          </div>
          <span className="text-xs text-green-400 font-semibold">Join →</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full flex flex-col justify-between p-5 overflow-hidden rounded-2xl"
      style={{ background: 'linear-gradient(135deg, #0a2e1a 0%, #0f4c2a 60%, #1a5c35 100%)' }}>

      {/* Background image — full bleed with dark overlay */}
      {item.imageUrl && (
        <div className="absolute inset-0 rounded-2xl overflow-hidden">
          <img src={item.imageUrl} alt={item.title}
            className="w-full h-full object-cover" />
          <div className="absolute inset-0"
            style={{ background: 'linear-gradient(160deg, rgba(4,16,8,0.75) 0%, rgba(6,22,12,0.65) 50%, rgba(3,12,6,0.80) 100%)' }} />
        </div>
      )}

      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-3">
          <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2.5 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            {item.status === 'LIVE' ? 'Live Now' : 'Upcoming'}
          </span>
        </div>

        <h3 className="font-serif text-white font-semibold text-base leading-snug mb-1.5 line-clamp-2">
          {item.title}
        </h3>
        {item.hostName && (
          <p className="text-white/70 text-xs mb-2">
            {item.hostName}{item.hostTitle ? ` · ${item.hostTitle}` : ''}
          </p>
        )}
        <div className="flex items-center gap-3 text-white/50 text-[10px] flex-wrap">
          <span className="flex items-center gap-1"><Calendar size={9} /> {date} · {time}</span>
          {item.location && <span className="flex items-center gap-1"><MapPin size={9} /> {item.location}</span>}
        </div>
      </div>

      {/* Bottom section */}
      <div className="relative z-10 space-y-2">

        {/* Promo video — play inline button */}
        {item.promoVideoUrl && (
          <button
            onClick={() => setShowVideo(true)}
            className="flex items-center gap-2 text-xs text-amber-300 hover:text-amber-200 transition-colors bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-1.5 w-full">
            <Play size={11} className="fill-amber-400 text-amber-400 flex-shrink-0" />
            Watch promo video
          </button>
        )}

        {/* Join code — clickable → goes to seminar room */}
        <Link to={`/seminar/${item.joinCode}`}
          className="bg-white/10 border border-white/20 hover:bg-white/20 rounded-xl px-3 py-2.5 flex items-center justify-between transition-colors group">
          <div>
            <p className="text-[9px] text-white/50 uppercase tracking-widest mb-0.5">Click to join · no login needed</p>
            <p className="font-mono font-bold text-white text-sm tracking-widest">{item.joinCode}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleCopy}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/30 transition-colors"
              title="Copy code">
              {copied
                ? <CheckCircle2 size={12} className="text-green-400" />
                : <Copy size={12} className="text-white/60" />}
            </button>
            <span className="text-green-400 text-xs font-semibold group-hover:translate-x-0.5 transition-transform">→</span>
          </div>
        </Link>
      </div>
    </div>
  );
};

const ExpertSlide = ({ item }) => {
  const name = `${item.user?.firstName ?? ''} ${item.user?.lastName ?? ''}`.trim();
  const specs = (() => {
    try {
      const s = item.user?.expertProfile?.specializations;
      return Array.isArray(s) ? s : JSON.parse(s || '[]');
    } catch { return []; }
  })();

  return (
    <div className="w-full h-full flex flex-col justify-between p-5 rounded-2xl"
      style={{ background: 'linear-gradient(135deg, #1a0a2e 0%, #2a0f4c 100%)' }}>
      <div>
        <span className="text-[10px] font-bold uppercase tracking-widest text-purple-300/70 flex items-center gap-1.5 mb-3">
          <Star size={10} className="fill-purple-400 text-purple-400" /> Featured Expert
        </span>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-purple-800 rounded-xl flex items-center justify-center text-white font-serif font-bold text-lg overflow-hidden flex-shrink-0">
            {item.user?.avatar
              ? <img src={item.user.avatar} alt={name} className="w-full h-full object-cover" />
              : name[0]}
          </div>
          <div>
            <p className="font-serif font-semibold text-white text-sm">{name}</p>
            {item.user?.expertProfile?.yearsOfExperience > 0 && (
              <p className="text-purple-300/60 text-[10px]">
                {item.user.expertProfile.yearsOfExperience} yrs experience
              </p>
            )}
          </div>
        </div>
        {item.quote && (
          <p className="text-white/60 text-xs leading-relaxed italic line-clamp-3">"{item.quote}"</p>
        )}
      </div>
      <div className="space-y-2">
        {specs.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {specs.slice(0, 3).map((s, i) => (
              <span key={i} className="text-[9px] bg-purple-500/20 text-purple-300 border border-purple-500/20 px-2 py-0.5 rounded-full">{s}</span>
            ))}
          </div>
        )}
        <Link to={`/experts/${item.userId}`}
          className="flex items-center gap-1.5 text-[10px] text-purple-300/60 hover:text-purple-200 transition-colors">
          View profile <ExternalLink size={9} />
        </Link>
      </div>
    </div>
  );
};

const MenteeSlide = ({ item }) => {
  const name = `${item.user?.firstName ?? ''} ${item.user?.lastName ?? ''}`.trim();
  const business = item.user?.clientProfile?.businessName;
  const bizType  = item.user?.clientProfile?.businessType;

  return (
    <div className="w-full h-full flex flex-col justify-between p-5 rounded-2xl"
      style={{ background: 'linear-gradient(135deg, #0a1e2e 0%, #0f3048 100%)' }}>
      <div>
        <span className="text-[10px] font-bold uppercase tracking-widest text-blue-300/70 flex items-center gap-1.5 mb-3">
          <Briefcase size={10} className="text-blue-400" /> Featured Mentee
        </span>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-blue-800 rounded-xl flex items-center justify-center text-white font-serif font-bold text-lg overflow-hidden flex-shrink-0">
            {item.user?.avatar
              ? <img src={item.user.avatar} alt={name} className="w-full h-full object-cover" />
              : name[0]}
          </div>
          <div>
            <p className="font-serif font-semibold text-white text-sm">{name}</p>
            {business && <p className="text-blue-300/60 text-[10px]">{business}</p>}
          </div>
        </div>
        {item.quote && (
          <p className="text-white/60 text-xs leading-relaxed italic line-clamp-3">"{item.quote}"</p>
        )}
      </div>
      <div>
        {bizType && (
          <span className="text-[9px] bg-blue-500/20 text-blue-300 border border-blue-500/20 px-2 py-0.5 rounded-full">{bizType}</span>
        )}
      </div>
    </div>
  );
};

// ── Main Carousel ─────────────────────────────────────────────
const HeroCarousel = () => {
  const [slides,   setSlides]   = useState([]);
  const [current,  setCurrent]  = useState(0);
  const [loading,  setLoading]  = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    seminarService.getCarousel()
      .then(res => {
        // ApiResponse.success -> res.data.data has { seminars, featuredExperts, featuredMentees }
        const d = res.data?.data ?? res.data ?? {};
        const seminars       = Array.isArray(d.seminars)        ? d.seminars        : [];
        const featuredExperts = Array.isArray(d.featuredExperts) ? d.featuredExperts : [];
        const featuredMentees = Array.isArray(d.featuredMentees) ? d.featuredMentees : [];

        const allSlides = [
          ...seminars.map(s        => ({ type: 'seminar', ...s })),
          ...featuredExperts.map(s => ({ type: 'expert',  ...s })),
          ...featuredMentees.map(s => ({ type: 'mentee',  ...s })),
        ];
        setSlides(allSlides);
      })
      .catch(err => {
        console.warn('Carousel unavailable:', err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  // Auto-slide every 5 seconds
  useEffect(() => {
    if (slides.length <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrent(c => (c + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [slides]);

  const prev = () => { clearInterval(timerRef.current); setCurrent(c => (c - 1 + slides.length) % slides.length); };
  const next = () => { clearInterval(timerRef.current); setCurrent(c => (c + 1) % slides.length); };

  if (loading || slides.length === 0) return null;

  const slide = slides[current];

  return (
    <div className="w-full max-w-sm flex-shrink-0 space-y-2 mt-16">
      {/* Slide */}
      <div className="relative h-64 rounded-2xl overflow-hidden shadow-2xl">
        {slide.type === 'seminar' && <SeminarSlide item={slide} />}
        {slide.type === 'expert'  && <ExpertSlide  item={slide} />}
        {slide.type === 'mentee'  && <MenteeSlide  item={slide} />}

        {/* Nav arrows */}
        {slides.length > 1 && (
          <>
            <button onClick={prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-colors z-20">
              <ChevronLeft size={14} />
            </button>
            <button onClick={next}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-colors z-20">
              <ChevronRight size={14} />
            </button>
          </>
        )}
      </div>

      {/* Dots */}
      {slides.length > 1 && (
        <div className="flex items-center justify-center gap-1.5">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`rounded-full transition-all ${
                i === current ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/30'
              }`} />
          ))}
        </div>
      )}

      {/* Slide type label */}
      <p className="text-center text-[10px] text-white/30 uppercase tracking-widest">
        {slide.type === 'seminar' ? '📅 Upcoming Programme' : slide.type === 'expert' ? '⭐ Featured Expert' : '🎯 Featured Mentee'}
        {' '}· {current + 1}/{slides.length}
      </p>
    </div>
  );
};

export default HeroCarousel;